// src/utils/noteLinks.js
//
// Extração, classificação e renderização dos links do corpo de uma nota.
// Compartilhado por NoteList.vue e NoteGeneralForm.vue (antes duplicado nos dois).
//
// Tipos de link:
//   image    → miniatura da própria imagem (o <img> é quem detecta a falha)
//   youtube  → miniatura do vídeo (img.youtube.com, sem backend)
//   document → ícone por extensão (pdf, doc, xls…)
//   website  → preview do site (imagem OpenGraph) quando o backend responde;
//              enquanto isso, favicon + domínio
//
// Estado de verificação (vem do backend, via useLinkPreviews):
//   { state: "unknown" | "ok" | "broken", title?, imageUrl? }
// "unknown" é o estado inicial: renderiza como antes, sem selo de erro.

export const ATTACHMENT_IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "avif",
  "ico",
  "tif",
  "tiff",
];

export const ATTACHMENT_DOCUMENT_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  "odt",
  "ods",
  "odp",
  "rtf",
];

// Hosts/padrões conhecidos que servem imagens sem extensão no caminho.
const IMAGE_HOST_PATTERNS =
  /(gstatic\.com\/images|encrypted-tbn|googleusercontent\.com|\/thumb\/|wikimedia\.org\/.*\.(?:jpg|jpeg|png|gif|webp|svg)|nocookie\.net\/.*\.(?:jpg|jpeg|png|gif|webp|svg))/i;

// Pares de aspas (retas e "inteligentes"). URL entre aspas = texto comum.
const QUOTE_PAIRS = {
  '"': '"',
  "'": "'",
  "“": "”",
  "‘": "’",
};

export function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Normaliza uma URL crua: remove pontuação final e garante o protocolo.
export function normalizeUrl(raw) {
  let url = String(raw).trim().replace(/[.,;:)\]]+$/, "");
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  return url;
}

// Padrão único de URL reutilizado na extração e na linkificação do corpo.
function buildUrlRegex() {
  const ext = [
    ...ATTACHMENT_IMAGE_EXTENSIONS,
    ...ATTACHMENT_DOCUMENT_EXTENSIONS,
  ].join("|");
  return new RegExp(
    `(?:https?:\\/\\/|www\\.)[^\\s<>"')\\]]+` +
      `|\\b[a-z0-9.-]+\\.[a-z]{2,}\\/[^\\s<>"')\\]]*\\.(?:${ext})\\b`,
    "gi"
  );
}

function isQuotedUrl(str, start, end) {
  const before = str[start - 1];
  const after = str[end];
  return (
    !!before &&
    QUOTE_PAIRS[before] !== undefined &&
    QUOTE_PAIRS[before] === after
  );
}

export function extractUrls(text) {
  if (!text) return [];
  const str = String(text);
  const re = buildUrlRegex();
  const found = [];
  let m;
  while ((m = re.exec(str)) !== null) {
    if (isQuotedUrl(str, m.index, m.index + m[0].length)) continue;
    found.push(normalizeUrl(m[0]));
  }
  return found;
}

function getFileExtension(url) {
  if (!url) return null;
  const clean = url.split(/[?#]/)[0];
  const match = clean.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : null;
}

// ID do vídeo em qualquer forma de link do YouTube (watch, youtu.be, shorts, embed).
export function getYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com|youtube-nocookie\.com)\/watch\?(?:.*&)?v=([\w-]{11})/i,
    /youtu\.be\/([\w-]{11})/i,
    /(?:youtube\.com|youtube-nocookie\.com)\/(?:embed|shorts|v|live)\/([\w-]{11})/i,
  ];
  for (const re of patterns) {
    const m = String(url).match(re);
    if (m) return m[1];
  }
  return null;
}

export function getYouTubeThumb(videoId) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

// Link de qualquer host do YouTube, mesmo sem um ID de vídeo extraível
// (ex.: URL atípica). Serve para mostrar o ícone de vídeo em vez do globo.
export function isYouTubeHost(url) {
  return /^https?:\/\/(?:[\w-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be)(?:[/:?#]|$)/i.test(
    String(url)
  );
}

export function classifyUrl(url) {
  const extension = getFileExtension(url);

  const videoId = getYouTubeId(url);
  if (videoId) return { type: "youtube", url, videoId, extension: "" };
  // Host do YouTube sem ID reconhecível: ainda é vídeo (ícone de vídeo, não globo).
  if (isYouTubeHost(url)) return { type: "youtube", url, videoId: null, extension: "" };

  if (extension && ATTACHMENT_IMAGE_EXTENSIONS.includes(extension)) {
    return { type: "image", url, extension };
  }
  if (IMAGE_HOST_PATTERNS.test(url)) {
    return { type: "image", url, extension: extension ?? "" };
  }
  if (extension && ATTACHMENT_DOCUMENT_EXTENSIONS.includes(extension)) {
    return { type: "document", url, extension };
  }
  // Sem extensão de arquivo conhecida → link de site (página da internet).
  return { type: "website", url, extension: extension ?? "" };
}

// Hostname "limpo" (sem www.) — usado como rótulo padrão do site.
export function getHostname(url) {
  const m = String(url).match(/^https?:\/\/([^/]+)/i);
  return m ? m[1].replace(/^www\./i, "") : String(url);
}

export function truncateLink(text, max = 35) {
  const str = String(text ?? "");
  if (str.length <= max) return str;
  return str.slice(0, max - 1) + "…";
}

// Ícone (Font Awesome) e cor conforme a extensão do documento.
export function getDocMeta(extension) {
  const e = (extension || "").toLowerCase();
  if (e === "pdf") return { icon: "fa-file-pdf", color: "#dc2626" };
  if (["doc", "docx", "odt", "rtf"].includes(e))
    return { icon: "fa-file-word", color: "#2563eb" };
  if (["xls", "xlsx", "csv", "ods"].includes(e))
    return { icon: "fa-file-excel", color: "#16a34a" };
  if (["ppt", "pptx", "odp"].includes(e))
    return { icon: "fa-file-powerpoint", color: "#ea580c" };
  if (e === "txt") return { icon: "fa-file-alt", color: "#6b7280" };
  return { icon: "fa-file", color: "#6b7280" };
}

// Ícone do estado "quebrado", específico por tipo de link.
function getBrokenIcon(type) {
  if (type === "image") return "fa-image";
  if (type === "youtube") return "fa-video-slash";
  if (type === "document") return "fa-file-excel";
  return "fa-globe";
}

// Miniatura de link quebrado: mesma moldura, em vermelho, com selo de alerta.
function buildBrokenHtml(hrefEsc, type, captionText) {
  return (
    `<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer" ` +
    `class="note-preview note-preview--broken" title="Link quebrado: ${hrefEsc}">` +
    `<span class="note-preview-thumb note-preview-thumb--broken">` +
    `<i class="fas ${getBrokenIcon(type)} note-preview-icon"></i>` +
    `<span class="note-preview-badge"><i class="fas fa-exclamation"></i></span>` +
    `</span>` +
    `<span class="note-preview-caption note-preview-caption--broken">` +
    `${escapeHtml(captionText)}</span>` +
    `</a>`
  );
}

const IMG_ERROR_FALLBACK =
  `onerror="this.style.display='none';` +
  `this.nextElementSibling.style.display='flex'"`;

// Vídeo removido/inexistente: o YouTube devolve 200 com um placeholder cinza de
// 120x90 em vez de 404 — o onerror não dispara. A miniatura real de um vídeo vivo
// tem 480px de largura (hqdefault), então largura <= 120 significa "não existe".
const YOUTUBE_PLACEHOLDER_CHECK =
  `onload="if(this.naturalWidth<=120){this.style.display='none';` +
  `this.nextElementSibling.style.display='flex';` +
  `this.closest('.note-preview').classList.add('note-preview--broken')}"`;

function googleFavicon(host) {
  return (
    `https://www.google.com/s2/favicons?domain=` +
    `${encodeURIComponent(host)}&sz=64`
  );
}

// Preview de página (site ou página do YouTube que não é vídeo, ex.: canal):
// imagem OpenGraph quando o backend fornece; senão, o favicon do domínio.
// `fallbackIconHtml` é exibido se a imagem/favicon não carregar (globo p/ site,
// ícone do YouTube p/ página do YouTube) — nunca o globo num link do YouTube.
// `overlayHtml` (opcional) é um selo sobreposto à miniatura (ex.: logo do YouTube).
function buildPagePreviewHtml(hrefEsc, host, status, captionFn, fallbackIconHtml, extraClass, overlayHtml = "") {
  const fallback =
    `<span class="note-preview-fallback" style="display:none">` +
    `${fallbackIconHtml}</span>`;
  const inner = status?.imageUrl
    ? `<img src="${escapeHtml(status.imageUrl)}" class="note-preview-img" ` +
      `alt="pré-visualização" loading="lazy" ${IMG_ERROR_FALLBACK} />${fallback}`
    : `<img src="${escapeHtml(googleFavicon(host))}" class="note-preview-favicon" ` +
      `alt="favicon" loading="lazy" ${IMG_ERROR_FALLBACK} />${fallback}`;
  return (
    `<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer" ` +
    `class="note-preview ${extraClass}" title="${hrefEsc}">` +
    `<span class="note-preview-thumb">${inner}${overlayHtml}</span>` +
    captionFn(status?.title || host) +
    `</a>`
  );
}

// Monta o HTML de um preview. `status` vem do backend (pode ser undefined).
export function buildPreviewHtml(linkText, label, status) {
  const href = normalizeUrl(linkText);
  const hrefEsc = escapeHtml(href);
  const info = classifyUrl(href);
  const state = status?.state ?? "unknown";

  const caption = (text) => {
    const full = label || text;
    return `<span class="note-preview-caption" title="${escapeHtml(full)}">${escapeHtml(
      truncateLink(full)
    )}</span>`;
  };

  if (state === "broken") {
    const fallbackCaption =
      info.type === "website" ? getHostname(href) : truncateLink(href);
    return buildBrokenHtml(hrefEsc, info.type, label || fallbackCaption);
  }

  if (info.type === "image") {
    // Imagem só recebe legenda quando há rótulo [..]; sem rótulo, fica sem nada.
    // O <img> detecta sozinho a falha: cai no selo de quebrado.
    return (
      `<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer" ` +
      `class="note-preview note-preview--image" title="${hrefEsc}">` +
      `<span class="note-preview-thumb">` +
      `<img src="${hrefEsc}" class="note-preview-img" ` +
      `alt="pré-visualização" loading="lazy" ${IMG_ERROR_FALLBACK} />` +
      `<span class="note-preview-fallback note-preview-fallback--broken" ` +
      `style="display:none">` +
      `<i class="fas fa-image"></i>` +
      `<span class="note-preview-badge"><i class="fas fa-exclamation"></i></span>` +
      `</span>` +
      `</span>` +
      (label ? caption("") : "") +
      `</a>`
    );
  }

  if (info.type === "youtube") {
    // Página do YouTube que não é vídeo (canal, playlist…): preview da página
    // (og:image + título) ou favicon do YouTube — nunca o globo. Quando há
    // imagem OG, o selo do YouTube fica sobreposto (como nas miniaturas de vídeo);
    // no fallback de favicon o selo seria redundante (o favicon JÁ é o logo).
    if (!info.videoId) {
      const overlay = status?.imageUrl
        ? `<span class="note-preview-play"><i class="fab fa-youtube"></i></span>`
        : "";
      return buildPagePreviewHtml(
        hrefEsc,
        getHostname(href),
        status,
        caption,
        `<i class="fab fa-youtube" style="color:#ff0000"></i>`,
        "note-preview--youtube",
        overlay
      );
    }
    // Miniatura do vídeo direto do YouTube — não depende do backend.
    const thumb = escapeHtml(getYouTubeThumb(info.videoId));
    return (
      `<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer" ` +
      `class="note-preview note-preview--youtube" title="${hrefEsc}">` +
      `<span class="note-preview-thumb">` +
      `<img src="${thumb}" class="note-preview-img" ` +
      `alt="pré-visualização do vídeo" loading="lazy" ` +
      `${IMG_ERROR_FALLBACK} ${YOUTUBE_PLACEHOLDER_CHECK} />` +
      `<span class="note-preview-fallback note-preview-fallback--broken" ` +
      `style="display:none">` +
      `<i class="fas fa-video-slash"></i>` +
      `<span class="note-preview-badge"><i class="fas fa-exclamation"></i></span>` +
      `</span>` +
      `<span class="note-preview-play"><i class="fab fa-youtube"></i></span>` +
      `</span>` +
      caption(status?.title || "YouTube") +
      `</a>`
    );
  }

  if (info.type === "document") {
    const doc = getDocMeta(info.extension);
    return (
      `<a href="${hrefEsc}" target="_blank" rel="noopener noreferrer" ` +
      `class="note-preview note-preview--doc" title="${hrefEsc}">` +
      `<span class="note-preview-thumb">` +
      `<i class="fas ${doc.icon} note-preview-icon" ` +
      `style="color:${doc.color}"></i>` +
      `</span>` +
      caption(status?.title || linkText) +
      `</a>`
    );
  }

  // Site: com o backend, imagem OpenGraph + título. Sem ele, favicon + domínio.
  return buildPagePreviewHtml(
    hrefEsc,
    getHostname(href),
    status,
    caption,
    `<i class="fas fa-globe"></i>`,
    "note-preview--website"
  );
}

/**
 * Converte o corpo da nota em HTML seguro, trocando cada link por um preview.
 * Previews consecutivos (separados só por espaços/quebras) são agrupados numa
 * "linha de mídia" flex, ficando lado a lado.
 *
 * @param {string} text
 * @param {object} [options]
 * @param {(value: string) => string} [options.renderText] — como escapar/realçar
 *        o texto entre os links (padrão: escapeHtml).
 * @param {(url: string) => object|undefined} [options.getStatus] — veredito do
 *        backend para a URL normalizada.
 */
export function linkifyBody(text, options = {}) {
  const renderText = options.renderText ?? escapeHtml;
  const getStatus = options.getStatus ?? (() => undefined);

  const str = String(text ?? "");
  const re = buildUrlRegex();

  const tokens = [];
  let lastIndex = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    const raw = m[0];

    // URL entre aspas: mantém como texto comum (sem preview nem link).
    if (isQuotedUrl(str, m.index, m.index + raw.length)) {
      tokens.push({ type: "text", value: str.slice(lastIndex, m.index) + raw });
      lastIndex = m.index + raw.length;
      continue;
    }

    const tailMatch = raw.match(/[.,;:)\]]+$/);
    const tail = tailMatch ? tailMatch[0] : "";
    const linkText = tail ? raw.slice(0, -tail.length) : raw;

    // Rótulo opcional no formato [rótulo] logo antes da URL.
    let before = str.slice(lastIndex, m.index);
    let label = null;
    const labelMatch = before.match(/\[([^\]\n]+)\][ \t]*$/);
    if (labelMatch) {
      label = labelMatch[1].trim();
      before = before.slice(0, labelMatch.index);
    }
    if (before) tokens.push({ type: "text", value: before });
    tokens.push({
      type: "media",
      value: buildPreviewHtml(linkText, label, getStatus(normalizeUrl(linkText))),
    });
    if (tail) tokens.push({ type: "text", value: tail });

    lastIndex = m.index + raw.length;
  }
  const rest = str.slice(lastIndex);
  if (rest) tokens.push({ type: "text", value: rest });

  // Reagrupa mídias consecutivas (com espaço/quebra entre elas) numa flex row.
  const isBlank = (t) => t.type === "text" && /^\s*$/.test(t.value);
  let result = "";
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === "media") {
      const group = [t.value];
      let j = i + 1;
      while (j < tokens.length) {
        if (tokens[j].type === "media") {
          group.push(tokens[j].value);
          j += 1;
        } else if (
          isBlank(tokens[j]) &&
          j + 1 < tokens.length &&
          tokens[j + 1].type === "media"
        ) {
          group.push(tokens[j + 1].value);
          j += 2;
        } else {
          break;
        }
      }
      result +=
        group.length > 1
          ? `<span class="note-media-row">${group.join("")}</span>`
          : group[0];
      i = j;
    } else {
      result += renderText(t.value);
      i += 1;
    }
  }
  return result;
}

/** Anexos da nota: links do corpo, da fonte e da observação, imagens primeiro. */
export function getNoteAttachments(note) {
  const urls = [
    ...extractUrls(note?.body),
    ...extractUrls(note?.source),
    ...extractUrls(note?.observation),
  ];
  const unique = [...new Set(urls)];
  const items = unique.map((url) => classifyUrl(url));
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const rank = (t) => (t.item.type === "image" ? 0 : 1);
      return rank(a) - rank(b) || a.index - b.index;
    })
    .map(({ item }) => item);
}

export function hasAttachment(note) {
  return getNoteAttachments(note).length > 0;
}
